import { ref, computed, Ref } from "vue";
import { useToast } from "@app/vue/providers/Toast";

/**
 * Shared hook for managing settings forms with dirty tracking and save/reset functionality
 */
export function useSettingsForm<T extends Record<string, any>>(config: {
  initialState: T;
  serialize?: (state: T) => string;
  onSave: (state: T) => Promise<T>;
  onLoad: () => Promise<T>;
}) {
  const {
    initialState,
    serialize = (state) => JSON.stringify(state),
    onSave,
    onLoad,
  } = config;

  const loading = ref(true);
  const saving = ref(false);
  const errorMessage = ref<string | null>(null);
  const state = ref<T>({ ...initialState }) as Ref<T>;
  const serializedInitial = ref<string>(serialize(state.value));
  const toast = useToast();

  const isDirty = computed(
    () => serialize(state.value) !== serializedInitial.value,
  );

  const loadSettings = async () => {
    loading.value = true;
    errorMessage.value = null;
    try {
      const loaded = await onLoad();
      state.value = loaded;
      serializedInitial.value = serialize(loaded);
    } catch (error) {
      console.error("Failed to load settings", error);
      errorMessage.value = "We could not load your settings. Please try again.";
    } finally {
      loading.value = false;
    }
  };

  const resetForm = () => {
    if (serializedInitial.value) {
      state.value = JSON.parse(serializedInitial.value);
    } else {
      state.value = { ...initialState };
    }
  };

  const handleSave = async () => {
    if (saving.value) return;
    saving.value = true;
    errorMessage.value = null;

    try {
      const updated = await onSave(state.value);
      state.value = updated;
      serializedInitial.value = serialize(updated);

      toast.showToast("Settings saved successfully.", {
        type: "success",
        autoHide: 4000,
      });
    } catch (error) {
      console.error("Failed to save settings", error);
      errorMessage.value =
        "Saving failed. Please review your entries and try again.";
      toast.showToast("Unable to save changes. Please try again.", {
        type: "error",
      });
    } finally {
      saving.value = false;
    }
  };

  return {
    loading,
    saving,
    errorMessage,
    state,
    isDirty,
    loadSettings,
    resetForm,
    handleSave,
  };
}
